import { useMutation, useQuery } from "@apollo/client";
import { Button, ButtonGroup, Card } from "@blueprintjs/core";
import { useState } from "react";
import {
  CreateProductGroupInput,
  ProductGroup,
  UpdateProductGroupInput,
} from "../../api/schema";
import {
  FlexContainer,
  FlexGrow,
  FlexRowStretch,
} from "../../components/flex/Flex";
import {
  DeleteModal,
  DeleteStateHelper,
} from "../../components/form/DeleteModal";
import { ModalInputType } from "../../components/form/Form";
import {
  InputFormMode,
  UpdateStateHelper,
  ValuesVariables,
} from "../../components/form/InputForm";
import { InputModal } from "../../components/form/InputModal";
import { ParentPrettyRender } from "../../components/ParentRenderer";
import { ListView } from "../../components/query/ListView";
import { Spinner } from "../../components/Spinner";
import { UserLanguage } from "../../lang/lang";
import {
  CreateProductGroupMutation,
  CreateProductGroupResponse,
  DeleteProductGroupMutation,
  DeleteProductGroupResponse,
  ProductGroupListQuery,
  ProductGroupListResponse,
  UpdateProductGroupMutation,
  UpdateProductGroupResponse,
} from "./graphql";

export const ProductGroupListView = (props: any) => {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [updateModalState, setUpdateModalState] = useState<
    UpdateStateHelper<ProductGroup>
  >({ open: false, item: undefined });
  const [deleteModalState, setDeleteModalState] = useState<DeleteStateHelper>({
    open: false,
    id: undefined,
    prettyPrint: undefined,
  });

  const [createProductGroup] = useMutation<
    CreateProductGroupResponse,
    ValuesVariables<ProductGroup>
  >(CreateProductGroupMutation);

  const [updateProductGroup] = useMutation<
    UpdateProductGroupResponse,
    ValuesVariables<UpdateProductGroupInput>
  >(UpdateProductGroupMutation);
  const [deleteProductGroup] = useMutation<DeleteProductGroupResponse>(
    DeleteProductGroupMutation
  );

  const listQuery = useQuery<ProductGroupListResponse>(ProductGroupListQuery, {
    fetchPolicy: "no-cache",
  });

  return (
    <FlexContainer>
      <Card elevation={3}>
        <FlexRowStretch>
          <div>
            <h2>{UserLanguage().pages.productGroup.list}</h2>
          </div>
          <FlexGrow />
          <div>
            <Button
              intent="success"
              icon="plus"
              onClick={() => setCreateModalOpen(true)}
            >
              {UserLanguage().actions.create}
            </Button>
          </div>
        </FlexRowStretch>
        <InputModal<
          ProductGroup,
          CreateProductGroupResponse,
          CreateProductGroupInput
        >
          keys={{
            name: {
              type: ModalInputType.String,
              icon: "id-number",
              placeholder: UserLanguage().data.name,
            },
            parent: {
              type: ModalInputType.ItemSelect,
              itemKey: "id",
              items: listQuery.data ? listQuery.data.productGroups : [],
              placeholder: UserLanguage().actions.select(
                UserLanguage().data.product_group.parent
              ),
              textRenderer: (i) => ParentPrettyRender(i),
              icon: "map-marker",
            },
          }}
          open={createModalOpen}
          texts={UserLanguage().pages.productGroup.actions}
          onClose={() => setCreateModalOpen(false)}
          mutation={createProductGroup}
          onSuccess={() => listQuery.refetch()}
          reformId={["parent"]}
          resetOnClose
          mode={InputFormMode.Create}
        />
        <InputModal<
          ProductGroup,
          UpdateProductGroupResponse,
          UpdateProductGroupInput
        >
          keys={{
            id: {
              type: ModalInputType.String,
              readonly: true,
              label: UserLanguage().data.id,
            },
            name: {
              type: ModalInputType.String,
              icon: "id-number",
              label: UserLanguage().data.name,
            },
            parent: {
              type: ModalInputType.ItemSelect,
              items: listQuery.data?.productGroups,
              itemKey: "name",
              label: UserLanguage().data.product_group.parent,
              textRenderer: (i) => ParentPrettyRender(i),
              placeholder: UserLanguage().actions.select(
                UserLanguage().data.product_group.parent
              ),
              icon: "map-marker",
            },
          }}
          open={updateModalState.open}
          input={updateModalState.item}
          texts={UserLanguage().pages.productGroup.actions}
          onClose={() => setUpdateModalState({ open: false, item: undefined })}
          mutation={updateProductGroup}
          reformId={["parent"]}
          onSuccess={() => {
            listQuery.refetch();
            setUpdateModalState({ open: false, item: undefined });
          }}
          resetOnClose
          mode={InputFormMode.Edit}
        />
        <DeleteModal<DeleteProductGroupResponse>
          id={deleteModalState.id}
          open={deleteModalState.open}
          mutation={(id) => deleteProductGroup({ variables: { id } })}
          texts={UserLanguage().pages.productGroup.actions}
          resetOnClose
          onClose={() => {
            setDeleteModalState({ open: false, id: undefined });
          }}
          onSuccess={() => {
            setDeleteModalState({ open: false, id: undefined });
            listQuery.refetch();
          }}
          type={UserLanguage().data.product_group.label}
          prettyPrint={deleteModalState.prettyPrint}
        />
        <ListView<ProductGroupListResponse, ProductGroup>
          cols={{
            id: { label: UserLanguage().data.id },
            name: { label: UserLanguage().data.name },
            parent: {
              label: UserLanguage().data.product_group.parent,
              render: (i) => ParentPrettyRender(i.parent),
            },
            actions: {
              label: UserLanguage().actions.label,
              render: (item) => (
                <ButtonGroup>
                  <Button
                    intent="primary"
                    rightIcon="edit"
                    onClick={() =>
                      setUpdateModalState({
                        open: true,
                        item,
                      })
                    }
                  >
                    {UserLanguage().actions.edit}
                  </Button>
                  <Button
                    intent="danger"
                    rightIcon="delete"
                    onClick={() =>
                      setDeleteModalState({
                        open: true,
                        id: item.id,
                        prettyPrint: `${UserLanguage().data.name}: ${
                          item.name
                        }`,
                      })
                    }
                  >
                    {UserLanguage().actions.delete}
                  </Button>
                </ButtonGroup>
              ),
            },
          }}
          error={<p>Error</p>}
          loading={<Spinner />}
          itemKey="productGroups"
          query={listQuery}
        />
      </Card>
    </FlexContainer>
  );
};
